import { useMutation } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/frontend/components/ui/collapsible";
import { orpc } from "@/frontend/orpc-client";
import { cn } from "@/frontend/lib/utils";
import { DESTINATION_TYPE } from "@/server/types/export";
import {
  computeExportProgress,
  formatTimeAgo,
  formatDuration,
} from "@/frontend/utils/dashboard/settings/export";
import { getDestinationLogo, getDestTypeLabel } from "@/frontend/utils/dashboard/settings/activity";
import { STATUS_BADGE_COLORS, TRIGGER_LABELS } from "@/frontend/types/dashboard/settings/activity";

export function HistoryRow({ run, onRefetch }: { run: any; onRefetch: () => void }) {
  const retry = useMutation({
    ...orpc.admin.retryExportRun.mutationOptions(),
    onSuccess: onRefetch,
  });

  const isDelta = run.delta_from != null;
  const isFhir = run.destination_type === DESTINATION_TYPE.FHIR_SERVER;
  const destTypeLabel = getDestTypeLabel(run);
  const hasError = (run.status === "failed" || run.status === "partial") && run.error_message;

  // FHIR runs track resource-wise progress post-refactor; keep S3 on the
  // legacy patients_total / patients_succeeded pair for its per-account UI.
  // Show "done / total" only when partial — fully-posted runs just show total.
  const progress = computeExportProgress(run);
  const resourcesLabel = isFhir
    ? progress.done < progress.total
      ? `${progress.done} / ${progress.total}`
      : String(progress.total)
    : String(run.items_exported ?? 0);
  const patientsLabel =
    run.status === "partial"
      ? `${run.patients_succeeded ?? 0}/${run.patients_total ?? 0}`
      : String(run.patients_total ?? 0);

  const completedAt = run.completed_at ?? run.started_at;
  const timeLabel = completedAt ? formatTimeAgo(completedAt) : null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer text-left group">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <img src={getDestinationLogo(run)} alt={destTypeLabel} className="w-5 h-5 object-contain shrink-0" />
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-medium truncate">{run.destination_name}</span>
              <span className="text-xs text-muted-foreground">{destTypeLabel}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {patientsLabel} patients · {resourcesLabel} resources · {formatDuration(run.started_at, run.completed_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn(
            "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize",
            STATUS_BADGE_COLORS[run.status] ?? "bg-gray-100 text-gray-700"
          )}>
            {run.status}
          </span>
          {timeLabel && (
            <span className="text-xs text-muted-foreground">{timeLabel}</span>
          )}
          <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4">
          <div className="grid grid-cols-[80px_1fr_80px_1fr] gap-x-3 gap-y-1.5 text-xs pt-3 border-t border-border">
            <span className="text-muted-foreground">Trigger</span>
            <span>{TRIGGER_LABELS[run.trigger] ?? run.trigger}</span>
            <span className="text-muted-foreground">Type</span>
            <span className={cn("font-medium", isDelta ? "text-blue-600 dark:text-blue-400" : "text-purple-600 dark:text-purple-400")}>
              {isDelta ? "Delta" : "Full"}
            </span>
            <span className="text-muted-foreground">Patients</span>
            <span>{patientsLabel}</span>
            <span className="text-muted-foreground">Resources</span>
            <span>{resourcesLabel}</span>
            <span className="text-muted-foreground">Duration</span>
            <span>{formatDuration(run.started_at, run.completed_at)}</span>
          </div>

          {hasError && (
            <div className="mt-3 flex items-center gap-3 py-2.5 px-4 rounded-lg bg-red-50 dark:bg-red-500/5">
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-400/70 mb-0.5">
                  Error
                </p>
                <p className="text-xs text-red-600 dark:text-red-300/85">
                  {run.error_message}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 h-7 text-xs"
                onClick={() => retry.mutate({ runId: run.id })}
                disabled={retry.isPending}
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
